/**
 * Content model for /services/hiatal-hernia-surgery.
 *
 * Copy supplied by the practice as a Word document (hiatal-hernia-page-draft).
 * Headings, FAQs, schema fields, and internal links follow that draft.
 */

import { formatReviewedDate, lastmodFor } from "@/lib/route-lastmod";
import hiatalNormal1254 from "@/assets/hiatal-normal-v3-1254.jpg.asset.json";
import hiatalType1_1254 from "@/assets/hiatal-type-1-v3-1254.jpg.asset.json";
import hiatalType2_1254 from "@/assets/hiatal-type-2-v3-1254.jpg.asset.json";
import hiatalType3_1254 from "@/assets/hiatal-type-3-v3-1254.jpg.asset.json";
import hiatalType4_1254 from "@/assets/hiatal-type-4-v3-1254.jpg.asset.json";
import sleeveHiatalScan1141 from "@/assets/sleeve-hiatal-type-1-1141.jpg.asset.json";
import secondSleeveHiatalScan1254 from "@/assets/sleeve-hiatal-type-1-second-en-1254.jpg.asset.json";
import type2SleeveScan1448 from "@/assets/hiatal-type-2-sleeve-en-1448.jpg.asset.json";
import type3SleeveScan1087 from "@/assets/hiatal-type-3-sleeve-en-1087.jpg.asset.json";
import type3SecondScan1122 from "@/assets/hiatal-type-3-second-en-1122.jpg.asset.json";
import type4PatientBefore from "@/assets/hiatal-type-iv-patient-before-1448.webp.asset.json";
import type4PatientRepair from "@/assets/hiatal-type-iv-patient-repair.png.asset.json";
import type4PatientWeightLoss from "@/assets/hiatal-type-iv-patient-weight-loss.png.asset.json";
import type4Story2Hernia from "@/assets/hiatal-type-4-story2-hernia.png.asset.json";
import type4Story2Midway from "@/assets/hiatal-type-4-story2-midway.png.asset.json";
import type4Story2Completed from "@/assets/hiatal-type-4-story2-completed.png.asset.json";
import type4Story2Weight from "@/assets/hiatal-type-4-story2-weight.png.asset.json";
import type4UpperGi from "@/assets/hiatal-type-iv-upper-gi-en-790.webp.asset.json";
import type1RepairEn from "@/assets/hiatal-type-1-repair-en-1059.webp.asset.json";
import type1EsophagusEn from "@/assets/hiatal-type-1-esophagus-en.png.asset.json";
import type1CompletedEn from "@/assets/hiatal-type-1-completed-en.png.asset.json";
import type1WeightEn from "@/assets/hiatal-type-1-weight-en.png.asset.json";
import type3StoryHernia from "@/assets/hiatal-type-3-story-hernia-1058.webp.asset.json";
import type3StoryRightCrus from "@/assets/hiatal-type-3-story-right-crus.png.asset.json";
import type3StorySleeveBougie from "@/assets/hiatal-type-3-story-sleeve-bougie.png.asset.json";
import type3StoryCruaRepair from "@/assets/hiatal-type-3-story-crua-repair.png.asset.json";
import type3StoryWeightLoss from "@/assets/hiatal-type-3-story-weight-loss.png.asset.json";
import type2StoryPreparation from "@/assets/hiatal-type-2-story-preparation-996.webp.asset.json";
import type2StoryArtery from "@/assets/hiatal-type-2-story-artery.png.asset.json";
import type2StoryCompletedRepair from "@/assets/hiatal-type-2-story-completed-repair.png.asset.json";
import type2StoryWeightData from "@/assets/hiatal-type-2-story-weight-data.png.asset.json";
import introFundusEn from "@/assets/hiatal-intro-fundus-en-990.webp.asset.json";
import recurrentScarTissueEn from "@/assets/scar-tissue-crura-esophagus-en-1456.webp.asset.json";
import recurrentCompletedEn from "@/assets/completed-repair-en.png.asset.json";
import recurrentRedo2En from "@/assets/hiatal-recurrent-redo-2-1430.webp.asset.json";
import recurrentRedo2CompleteEn from "@/assets/hiatal-recurrent-redo-2-complete.png.asset.json";

export const HIATAL_PATH = "/services/hiatal-hernia-surgery";

export const HIATAL_META = {
  path: HIATAL_PATH,
  title: "Hiatal Hernia Surgery in Northern Virginia | Virginia Bariatric",
  description:
    "Hiatal hernia repair in Northern Virginia with Dr. Matthew Fitzer: laparoscopic repair, fundoplication, redo repairs, and reflux after a sleeve.",
};

export type HiatalImageKey = "normal" | "type1" | "type2" | "type3" | "type4" | "intro";

export type HiatalFigure = {
  key: HiatalImageKey;
  /** Full-size JPEG (schema + lightbox). */
  full: string;
  alt: string;
  caption: string;
  width: number;
  height: number;
  /** True when the figure was created by the doctor with AI assistance. */
  aiAssisted?: boolean;
};

export const HIATAL_FIGURES: Record<HiatalImageKey, HiatalFigure> = {
  normal: {
    key: "normal",
    aiAssisted: true,
    full: hiatalNormal1254.url,
    alt: "Medical illustration of normal anatomy: the esophagus passes through a snug opening (hiatus) in the diaphragm to reach the stomach.",
    caption:
      "Normal anatomy. The hiatus — the opening in the diaphragm muscle — fits the esophagus snugly. All of the stomach is contained within the abdomen, which is its normal position.",
    width: 1254,
    height: 1254,
  },
  type1: {
    key: "type1",
    aiAssisted: true,
    full: hiatalType1_1254.url,
    alt: "Medical illustration of a type I (sliding) hiatal hernia: a small portion of the upper stomach slides up through the diaphragm's hiatus.",
    caption:
      "Type I hiatal hernia (sliding). A small portion of the stomach slides up through the hiatus.",
    width: 1254,
    height: 1254,
  },
  type2: {
    key: "type2",
    aiAssisted: true,
    full: hiatalType2_1254.url,
    alt: "Medical illustration of a type II (paraesophageal) hiatal hernia: the esophagus stays in place while part of the stomach slides up alongside it.",
    caption:
      "Type II hiatal hernia (paraesophageal). The esophagus stays in place while part of the stomach slides up alongside it.",
    width: 1254,
    height: 1254,
  },
  type3: {
    key: "type3",
    aiAssisted: true,
    full: hiatalType3_1254.url,
    alt: "Medical illustration of a type III (mixed) hiatal hernia: the esophagus and stomach both migrate into the chest above the diaphragm.",
    caption:
      "Type III hiatal hernia (mixed). A significant portion of the stomach herniates into the chest alongside the esophagus.",
    width: 1254,
    height: 1254,
  },
  type4: {
    key: "type4",
    aiAssisted: true,
    full: hiatalType4_1254.url,
    alt: "Medical illustration of a type IV hiatal hernia: the stomach and a loop of transverse colon herniate through the diaphragm.",
    caption:
      "Type IV hiatal hernia. The stomach and other organs — here, a loop of transverse colon — herniate through the diaphragm.",
    width: 1254,
    height: 1254,
  },
  intro: {
    key: "intro",
    full: introFundusEn.url,
    alt: "Annotated laparoscopic photo of a hiatal hernia: a dashed line outlines the hernia opening and an arrow labels the stomach fundus emerging from the hernia.",
    caption:
      "View of a large hernia: the fundus of the stomach (arrow) passes through the opening (dashed line) in the diaphragm.",
    width: 990,
    height: 559,
  },
};

export const HIATAL_TYPE4_PATIENT_STORY: HiatalFigure[] = [
  {
    key: "type4",
    full: type4PatientBefore.url,
    alt: "Annotated laparoscopic view showing most of a 29-year-old patient's stomach and omentum within a large type IV hiatal hernia before repair.",
    caption: "In 2025, this 29-year-old gentleman from Northern Virginia was referred to a general surgeon for repair of a large hiatal hernia that was causing dire acid reflux. Most of the stomach and the omentum were found within the hernia. The dashed line indicates the hernia opening.",
    width: 1448,
    height: 1086,
  },
  {
    key: "type4",
    full: type4PatientRepair.url,
    alt: "Annotated laparoscopic view of the completed type IV hiatal hernia repair, showing the esophagus and suture closure of the hernia.",
    caption: "Hernia repair in the severely obese requires simultaneous construction of a gastric bypass. Without that addition, the repair is likely to fail. The original surgeon promptly referred the patient to Dr. Fitzer for definitive care. The image shows the finished repair. The bottom aspect of the dashed lines indicates where Dr. Fitzer sewed the diaphragm edges together. The circular part of the dashed line shows the esophagus emerging through the repaired diaphragm.",
    width: 990,
    height: 559,
  },
  {
    key: "type4",
    full: type4PatientWeightLoss.url,
    alt: "Weight-loss curve after gastric bypass and type IV hiatal hernia repair, showing weight falling from about 344 pounds to 207 pounds over nearly eighteen months.",
    caption: "This image shows the patient's weight loss curve. Surgery was almost eighteen months ago, and he remains symptom-free and no longer takes heartburn medicine. Weight loss from the bypass dramatically lowers the pressure in the abdomen. It is a key reason why failure of a repair is much less likely when a bypass is performed at the same time.",
    width: 1561,
    height: 361,
  },
  {
    key: "type4",
    full: type4Story2Hernia.url,
    alt: "Annotated laparoscopic view of an enormous hiatal hernia, with an arrow marking the antrum of the stomach exiting the hiatus.",
    caption: "This 49-year-old woman from Northern Virginia underwent hiatal hernia repair and gastric bypass with Dr. Fitzer in 2017. You can only see the antrum peeking out of the truly enormous hernia.",
    width: 1105,
    height: 625,
  },
  {
    key: "type4",
    full: type4Story2Midway.url,
    alt: "Annotated laparoscopic view midway through a giant hiatal hernia repair, with the stomach nearly restored to the abdomen and the esophagus becoming visible inside the empty hernia.",
    caption: "Midway through the repair: The stomach has almost been restored to the abdomen; there's just a little more to do. The now-empty hernia looks cavernous.",
    width: 1105,
    height: 622,
  },
  {
    key: "type4",
    full: type4Story2Completed.url,
    alt: "Annotated laparoscopic view of the completed hiatal hernia repair, with the esophagus exiting the repaired hiatus and gastric bypass staple lines in the background.",
    caption: "The completed repair: you can see the staple lines on her pouch and stomach remnant in the background.",
    width: 1448,
    height: 804,
  },
  {
    key: "type4",
    full: type4Story2Weight.url,
    alt: "Weight chart after gastric bypass and hiatal hernia repair, showing weight falling from about 268 pounds to 177 pounds over roughly nine months.",
    caption: "We lost track of her at roughly nine months after surgery due to a work-related move. Her weight loss was solidly on track. She continued to have some degree of reflux symptoms.",
    width: 1582,
    height: 426,
  },
];

/** Upper GI series displayed between the Type IV diagram and patient story. */
export const HIATAL_TYPE4_UPPER_GI: HiatalFigure = {
  key: "type4",
  full: type4UpperGi.url,
  alt: "Annotated upper GI contrast study showing the body and fundus of the stomach within a giant type IV hiatal hernia, with the stomach antrum below it.",
  caption: "Giant hiatal hernia: this Upper GI series belongs to the gentleman whose hernia repair is documented in this image sequence. The hernia contained almost the entirety of the greater omentum and epiploic appendages of the transverse colon. It is therefore classified as type IV.",
  width: 790,
  height: 794,
};

/** English-labeled contrast study shown beside the type I illustration. */
export const HIATAL_SLEEVE_SCAN: HiatalFigure = {
  key: "type1",
  aiAssisted: true,
  full: sleeveHiatalScan1141.url,
  alt: "Annotated upper GI contrast study of a sleeve patient with a small hiatal hernia: the sleeve narrows where it crosses the diaphragm, with triangles marking the diaphragm's upper edge.",
  caption: "A sleeve patient has a type I hernia that is causing difficulty swallowing. Note how the sleeve is pinched and narrowed by the hiatus where it crosses the diaphragm. The small triangles mark the upper limit of the dome of the diaphragm.",
  width: 1141,
  height: 1378,
};

/** Second English-labeled type I sleeve study, displayed third in the row. */
export const HIATAL_SLEEVE_SCAN_SECOND: HiatalFigure = {
  key: "type1",
  aiAssisted: true,
  full: secondSleeveHiatalScan1254.url,
  alt: "Annotated upper GI contrast study of another sleeve patient with a type I hiatal hernia and narrowing at the diaphragm, with labels identifying the esophagus, lower esophageal sphincter, trapped sleeve, and tight hiatus.",
  caption: "Another sleeve patient with a type I hiatal hernia. They also have a narrowing where the esophagus goes through the diaphragm.",
  width: 1254,
  height: 1254,
};

/** English-labeled laparoscopic photo of a repaired sliding (type I) hernia. */
export const HIATAL_TYPE1_REPAIR: HiatalFigure = {
  key: "type1",
  full: type1RepairEn.url,
  alt: "Laparoscopic view of a repaired sliding type I hiatal hernia, with an arrow labeling the stomach emerging from the small hiatal hernia.",
  caption: "Repair of a sliding hiatal hernia: The patient is a 49-year-old female from Northern Virginia who underwent simultaneous gastric bypass in 2019.",
  width: 1059,
  height: 583,
};

/** English-labeled laparoscopic photo of the esophagus exiting the hiatus mid-repair. */
export const HIATAL_TYPE1_ESOPHAGUS: HiatalFigure = {
  key: "type1",
  full: type1EsophagusEn.url,
  alt: "Laparoscopic view midway through repair of a sliding hiatal hernia, with an arrow labeling the esophagus exiting the hiatus.",
  caption: "Midway through repair of a sliding hiatal hernia: The hernia sac was removed and the esophagus has been mobilized to reach into the abdomen with no tension.",
  width: 1062,
  height: 587,
};

/** English-labeled laparoscopic photo of the completed sliding (type I) repair. */
export const HIATAL_TYPE1_COMPLETED: HiatalFigure = {
  key: "type1",
  full: type1CompletedEn.url,
  alt: "Laparoscopic view of a completed sliding hiatal hernia repair, with dashed lines outlining the esophagus and an arrow labeling it exiting the repaired hiatus.",
  caption: "The completed hernia repair: the crura were gently sewn together to resize the hiatus, and the esophagus was tacked to the edges to make sure breathing-related suction would not pull it back through.",
  width: 844,
  height: 489,
};

/** English-labeled weight chart, slide 4 of the type I repair-story lightbox. */
export const HIATAL_TYPE1_WEIGHT: HiatalFigure = {
  key: "type1",
  full: type1WeightEn.url,
  alt: "Weight chart seven years after gastric bypass and sliding hiatal hernia repair, showing weight falling from about 341 pounds and then holding steady near 200 pounds.",
  caption: "Seven years later, her weight loss has been pretty stable. She remains 143 lbs. lighter than she was on the day of surgery. She also remains reflux-free.",
  width: 1590,
  height: 478,
};

export const HIATAL_RECURRENT_SCAR_TISSUE: HiatalFigure = {
  key: "type1",
  full: recurrentScarTissueEn.url,
  alt: "Laparoscopic view of scar tissue covering the crura and esophagus during redo hiatal hernia repair after a prior mesh repair.",
  caption: "Redo hiatal hernia repair after mesh repair: This 38-year-old from Maryland presented with reflux after a hiatal hernia repair elsewhere. Note how the visible surfaces are covered with scar tissue that was divided to separate the organs.",
  width: 1456,
  height: 819,
};

export const HIATAL_RECURRENT_COMPLETED_REPAIR: HiatalFigure = {
  key: "type1",
  full: recurrentCompletedEn.url,
  alt: "Laparoscopic view of the completed redo hiatal hernia repair, with the crura closed and the esophagus resting on the stomach.",
  caption: "Completed redo repair: It usually entails dividing a bunch of scar tissue, which is tedious work, but it can be done for those that need it. This patient reported resolution of her reflux symptoms.",
  width: 1464,
  height: 819,
};

export const HIATAL_RECURRENT_REDO_2: HiatalFigure = {
  key: "type1",
  full: recurrentRedo2En.url,
  alt: "Laparoscopic view during redo hiatal hernia repair, with the hiatus outlined by a dashed blue line and thick scar tissue around the esophagus.",
  caption: "Another redo hiatal hernia repair: this one in a 60-year-old female from West Virginia. She developed severe, recurrent reflux after a repair done a decade prior. Thick, whitish scar tissue had to be divided to get the esophagus and diaphragm apart.",
  width: 1430,
  height: 816,
};

export const HIATAL_RECURRENT_REDO_2_COMPLETE: HiatalFigure = {
  key: "type1",
  full: recurrentRedo2CompleteEn.url,
  alt: "Laparoscopic view of a redo hiatal hernia repair that is almost complete, with sutures in place to tack the esophagus to the downsized hiatus.",
  caption: "Redo repair almost complete: the one remaining task is to tack the esophagus to the downsized hiatus so the esophagus cannot slide back into the chest. She had major but incomplete improvement in her heartburn. She went from taking multiple daily heartburn medications to needing something only now and then.",
  width: 1455,
  height: 798,
};

/** English-labeled upper GI series shown beside the type II illustration. */
export const HIATAL_TYPE2_SLEEVE_SCAN: HiatalFigure = {
  key: "type2",
  aiAssisted: true,
  full: type2SleeveScan1448.url,
  alt: "Annotated upper GI series after sleeve gastrectomy showing a type II hiatal hernia, with the esophagus, unmoved gastroesophageal junction, herniated fundus, and sleeved stomach labeled.",
  caption: "This patient had a sleeve elsewhere and presented with severe reflux and dysphagia (swallowing trouble). The UGI series shows a type II hernia.",
  width: 1448,
  height: 1086,
};

/** Four-image Type II patient story; the first image is the row thumbnail. */
export const HIATAL_TYPE2_PATIENT_STORY: HiatalFigure[] = [
  {
    key: "type2",
    full: type2StoryPreparation.url,
    alt: "Laparoscopic view before type II hiatal hernia repair, labeling the stomach fundus exiting the hernia and an anomalous common hepatic artery.",
    caption: "Preparing to repair a type II hiatal hernia in a 49-year-old woman from Northern Virginia. She got a sleeve during the same procedure.",
    width: 996,
    height: 559,
  },
  {
    key: "type2",
    full: type2StoryArtery.url,
    alt: "Laparoscopic view after resection of the hernia sac, labeling the diaphragm crura, esophagus, and anomalous common hepatic artery.",
    caption: "Hiatal hernia repair underway: just after resection of the hernia sac. This image better shows her abnormal common hepatic artery. In maybe 1% of people, this major artery originates on the stomach. It's a hazard that surgeons occasionally must work around.",
    width: 1105,
    height: 617,
  },
  {
    key: "type2",
    full: type2StoryCompletedRepair.url,
    alt: "Laparoscopic view of the completed sleeve and nearly complete type II hiatal hernia repair, labeling the esophagus, repaired hiatus, sleeve staple line, and anomalous common hepatic artery.",
    caption: "Completed sleeve and nearly complete hiatal hernia repair: The hiatus has been returned to its proper size. All that remains is to tack the esophagus to the edges of the diaphragm muscle.",
    width: 1459,
    height: 806,
  },
  {
    key: "type2",
    full: type2StoryWeightData.url,
    alt: "Weight chart from 2018 through July 2026 showing sustained weight loss after sleeve gastrectomy and type II hiatal hernia repair.",
    caption: "Weight data, 2018 to July 2026: Her weight loss result has been stable. She also experienced near-total relief from the reflux that she had before surgery. Hiatal hernia repair at the time of VSG surgery can eliminate reflux, but it is far from guaranteed.",
    width: 1590,
    height: 486,
  },
];

/** English-labeled upper GI series shown beside the type III illustration. */
export const HIATAL_TYPE3_SLEEVE_SCAN: HiatalFigure = {
  key: "type3",
  aiAssisted: true,
  full: type3SleeveScan1087.url,
  alt: "Annotated upper GI series of a type III hiatal hernia after sleeve gastrectomy, labeling the esophagus, displaced gastroesophageal junction, herniated fundus, narrowed hiatus, and sleeve remaining in the abdomen.",
  caption: "Type III hernia in a patient who presented with weight regain and dire heartburn ten years after a sleeve done elsewhere. The UGI series showed an over-large sleeve, with the top half entrapped in a large hernia.",
  width: 1087,
  height: 1447,
};

/** Second English-labeled type III sleeve study, displayed third in the row. */
export const HIATAL_TYPE3_SLEEVE_SCAN_SECOND: HiatalFigure = {
  key: "type3",
  aiAssisted: true,
  full: type3SecondScan1122.url,
  alt: "Annotated upper GI study of another sleeve patient with a type III hiatal hernia, showing the esophagus, herniated stomach fundus, approximate gastroesophageal junction, diaphragm narrowing, and remaining sleeve in the abdomen.",
  caption: "This patient was sent to Dr. Fitzer for hiatal hernia repair. She had a sleeve done elsewhere more than a decade prior. She presented with severe reflux. Imaging shows half the sleeve stuck in a hiatal hernia. Weight loss was restored and her reflux resolved after hernia repair with conversion to bypass. The blue triangles mark the top of the diaphragm.",
  width: 1122,
  height: 1402,
};

/** English-annotated laparoscopic photos in the type III lightbox story. */
export const HIATAL_TYPE3_PATIENT_STORY: HiatalFigure[] = [
  {
    key: "type3",
    full: type3StoryHernia.url,
    alt: "Annotated laparoscopic view of a type III hiatal hernia, with an arrow and dashed line marking the top of the stomach inside the hernia.",
    caption: "This type III hiatal hernia was found incidentally in a 51-year-old man from Maryland at the time of his VSG. The arrow and dashed line mark the top of the stomach inside the hernia.",
    width: 1058,
    height: 593,
  },
  {
    key: "type3",
    full: type3StoryRightCrus.url,
    alt: "Annotated laparoscopic view labeling the right crus of the diaphragm and the esophagus, outlined with dashed lines.",
    caption: "Laparoscopic view mid-repair, after resection of the hernia sac. The dashed line marks the edges of the hiatus. The gap in the muscular diaphragm is much larger than it should be.",
    width: 1067,
    height: 577,
  },
  {
    key: "type3",
    full: type3StorySleeveBougie.url,
    alt: "Annotated laparoscopic view of a sleeve gastrectomy, labeling the sleeve with the bougie inside and the part of the stomach to be removed.",
    caption: "Sleeve gastrectomy in progress: the sleeve with the bougie inside, and the part of the stomach to be removed.",
    width: 1022,
    height: 559,
  },
  {
    key: "type3",
    full: type3StoryCruaRepair.url,
    alt: "Annotated laparoscopic view of the repaired hiatus, labeling the right crus, left crus, and esophagus along the suture line.",
    caption: "The repair finished: the right and left crura are sewn together, and the esophagus emerges through the resized opening.",
    width: 1061,
    height: 591,
  },
  {
    key: "type3",
    full: type3StoryWeightLoss.url,
    alt: "Weight-loss chart showing the patient's weight falling from 273 pounds before surgery to a low of 169 pounds, then stabilizing at 175 pounds two years after surgery.",
    caption: "The patient's weight-loss curve two years after surgery: weight loss was excellent, but he continued to have heartburn. Repairing a hiatal hernia at the time of sleeve surgery helps, but it doesn't eliminate the possibility of post-sleeve reflux.",
    width: 1589,
    height: 471,
  },
];

const SITE_ORIGIN = "https://virginiabariatric.com";

/** Schema.org image records sourced from the same figures rendered on-page. */
export function hiatalImageObjects(
  figures: Record<HiatalImageKey, HiatalFigure>,
  language: "en-US" | "es-US",
  pageUrl: string,
  additionalFigures: HiatalFigure[] = [],
) {
  return [...Object.values(figures), ...additionalFigures].map((figure) => {
    const absolute = figure.full.startsWith("http")
      ? figure.full
      : `${SITE_ORIGIN}${figure.full}`;

    return {
      "@type": "ImageObject" as const,
      contentUrl: absolute,
      url: absolute,
      caption: figure.caption,
      name: figure.alt,
      width: figure.width,
      height: figure.height,
      inLanguage: language,
      creditText: figure.aiAssisted
        ? "Matthew Fitzer, MD, with AI assistance"
        : "Virginia Bariatric Surgery",
      copyrightNotice: "© Virginia Bariatric Surgery",
      copyrightHolder: { "@type": "Organization", name: "Virginia Bariatric Surgery" },
      creator: figure.aiAssisted
        ? { "@type": "Person", name: "Matthew Fitzer, MD" }
        : { "@type": "Organization", name: "Virginia Bariatric Surgery" },
      license: pageUrl,
      acquireLicensePage: `${SITE_ORIGIN}/contact`,
      usageInfo: `${SITE_ORIGIN}/contact`,
    };
  });
}

/** Image-sitemap rows sourced from the same figures rendered on-page. */
export function hiatalSitemapImages(figures: Record<HiatalImageKey, HiatalFigure>, additionalFigures: HiatalFigure[] = []) {
  return [...Object.values(figures), ...additionalFigures].map((figure) => ({
    loc: figure.full.startsWith("http") ? figure.full : `${SITE_ORIGIN}${figure.full}`,
    caption: figure.caption,
    title: figure.alt,
  }));
}

export type HiatalTypeBlock = {
  title: string;
  text: string;
  figure: HiatalImageKey;
};

export type HiatalSection = {
  heading: string;
  id: string;
  paragraphs?: string[];
  /** Paragraphs rendered full-width after the section figure, outside its side column. */
  afterFigureParagraphs?: string[];
  /** Bullet groups rendered after the paragraphs. */
  bulletGroups?: { label?: string; items: string[] }[];
  /** H3 subsections rendered after the paragraphs, before afterBulletParagraphs. */
  subsections?: { id: string; title: string; paragraphs: string[] }[];
  /** H3 subsections rendered mid-section, after the paragraphs and before endParagraphs. */
  midSubsections?: { id: string; title: string; paragraphs: string[] }[];
  /** Render subsections immediately beneath the section heading, before the paragraphs. */
  subsectionsBefore?: boolean;
  /** Paragraphs rendered after the bullet groups, before the next section heading. */
  afterBulletParagraphs?: string[];
  /** Type I–IV blocks, each rendered with its diagram beside the text. */
  typeBlocks?: HiatalTypeBlock[];
  /** Paragraphs rendered after the type blocks, before the next section heading. */
  endParagraphs?: string[];
  /** Render this figure after the bullet groups. */
  afterBulletFigure?: HiatalImageKey;
  /** Render this figure after the paragraphs (used for normal anatomy). */
  figure?: HiatalImageKey;
};

export type HiatalPageContent = {
  h1: string;
  kicker: string;
  intro: string[];
  procedureName: string;
  procedureAlternateNames: string[];
  procedureDescription: string;
  webPageName: string;
  sections: HiatalSection[];
  faqs: { question: string; answer: string }[];
  relatedPages: { label: string; to: string; note: string }[];
  closing: string;
  /** Physician-asserted clinical review date; never derive from file modification time. */
  lastReviewed: string;
  byline: string;
  /** Numbered footnotes rendered after the byline, before the references. */
  footnotes?: string[];
  references: string[];
};

export const HIATAL_PAGE: HiatalPageContent = {
  h1: "Hiatal Hernia Surgery and Repair in Northern Virginia",
  kicker: "What a hiatal hernia is, and what it means for you",
  intro: [
    "Obesity is an important risk factor for developing a hiatal hernia, and both are often diagnosed in the same patient. The two problems are so closely intertwined that surgery for one usually requires a decision about how to handle the other. To understand the connection between them, it will help to review the basics of hiatal hernia anatomy.",
  ],
  procedureName: "Hiatal Hernia Repair",
  procedureAlternateNames: [
    "Laparoscopic hiatal hernia repair",
    "Paraesophageal hernia repair",
    "Nissen fundoplication",
    "Toupet fundoplication",
    "Hiatal hernia surgery",
  ],
  procedureDescription:
    "Laparoscopic repair of a hiatal hernia: reduction of the herniated stomach, removal of the hernia sac, mobilization of the esophagus, sutured closure of the crura, and, when indicated, a fundoplication. Frequently performed at the same time as bariatric surgery.",
  webPageName: "Hiatal Hernia Surgery and Repair in Northern Virginia",
  sections: [
    {
      heading: "What is a hiatal hernia?",
      id: "what-is-a-hiatal-hernia",
      paragraphs: [
        "Your esophagus connects your mouth to your stomach, which sits in the abdomen. To get down to the stomach, the esophagus must pass through the chest.",
        "The last barrier between the chest and the abdominal space is the diaphragm, which is a muscle. It looks like a dome-shaped piece of roast beef. (See the diagram.)",
        "We're all born a small hole at the top of the diaphragm. It's where the esophagus goes through it. The opening is called the esophageal “hiatus,” which is why we call these “hiatal” hernias.",
      ],
      figure: "normal",
      afterFigureParagraphs: [
        "The diaphragm is meant to fit the esophagus snugly at the hiatus. There shouldn’t be extra room. When the hiatus is too wide, the extra space there is a hiatal hernia. Over time, the top of the stomach tends to get pushed up through it into the chest.",
        "These hernias come in four types.",
      ],
      typeBlocks: [
        {
          title: "Type I, \u201csliding\u201d",
          text: "These are the small ones. Less than an inch or so of the stomach is in the chest. It tends to \u201cslide\u201d in and out of the chest as you breathe. Most hernias are type I. Type I hernias can (but don\u2019t always!) cause reflux.",
          figure: "type1",
        },
        {
          title: "Type II, \u201cparaesophageal\u201d",
          text: "The esophagus mostly stays put, but part of the stomach rolls into the chest alongside it.",
          figure: "type2",
        },
        {
          title: "Type III, \u201cmixed\u201d",
          text: "Another kind of paraesophageal hernia. Both the esophagus and stomach move up into the chest. Sometimes it's hard to tell if a hernia is type II or III. Most hernias that get to surgery are type III.",
          figure: "type3",
        },
        {
          title: "Type IV, \u201cgiant\u201d",
          text: "Another paraesophageal hernia. The gap in the diaphragm is so big that another organ — colon, spleen, pancreas, etc. — got sucked up into the chest with the stomach.",
          figure: "type4",
        },
      ],
      endParagraphs: [
        "The type of hernia is important to surgeons. It tells them, for instance, the likelihood that a hernia will cause an urgent problem. It also tells them (generally) what symptoms can be expected.",
        "Small hernias tend to be symptom-free, though they sometimes lead to reflux or heartburn. Many of them are found unintentionally on imaging that was done for another purpose.",
        "Large hernias cause heartburn as well, and they sometimes make other kinds of mischief. For instance, they sometimes cause food to come back up on its own (regurgitation), chest pain, trouble swallowing, or an abnormal feeling of fullness after a few bites of food. *Very* large hernias are known to occasionally create life-threatening problems, such as bleeding or blockage of the GI tract.",
      ],
    },
    {
      heading: "Hiatal hernia symptoms: common and rare",
      id: "symptoms",
      bulletGroups: [
        {
          label: "The common ones:",
          items: [
            "Heartburn, especially lying down or after a large meal",
            "Regurgitation \u2014 food or acid coming back up, sometimes at night",
            "Chest pain that can mimic cardiac pain",
            "Trouble swallowing, or food getting stuck",
          ],
        },
        {
          label: "The rare ones:",
          items: [
            "Early satiety (getting full after a few bites)",
            "Chronic cough (stomach acid inflames the airways)",
            "Hoarseness (stomach acid inflames the vocal cords)",
            "Worsening asthma (stomach acid causes bronchspasm)",
            "Shortness of breath after eating (a full stomach in a big hernia leaves no room for air to come in)",
            "Unexplained anemia (erosions cause bleeding where the stomach is pinched at the hiatus)",
          ],
        },
      ],
      afterBulletFigure: "intro",
    },
    {
      heading: "How hiatal hernias and obesity are linked",
      id: "hiatal-hernia-and-weight",
      subsectionsBefore: true,
      paragraphs: [
        "People with obesity store fat inside their body as well as on the outside. One place fat builds up is in the abdomen, where there isn't much extra space. When enough fat accumulates there, the pressure starts to rise. (A round, protruding belly is a sure sign of high pressure in the abdomen.)",
      ],
      midSubsections: [
        {
          id: "high-abdominal-pressure-widens-the-hiatus",
          title: "High abdominal pressure tends to widen the hiatus",
          paragraphs: [
            "The defining feature of pressure is that it pushes outward against its container in all directions. (Think of a balloon. Wherever you poke it, it pushes back against your finger.)",
            "When pressure builds up in the abdomen, it pushes outward on the edges of the hiatus. Given time, that stretching force will widen the hiatus. A hiatal hernia results.",
          ],
        },
        {
          id: "obesity-and-hiatal-overlap",
          title: "Obesity and hiatal hernia overlap in other ways",
          paragraphs: [],
        },
      ],
      endParagraphs: [
        "There is a lot of overlap between hiatal hernia repair and bariatric surgery in practice:",
      ],
      bulletGroups: [
        {
          items: [
            "Hiatal hernias are often discovered during bariatric surgery. Some need to be repaired on the spot, especially during a sleeve gastrectomy.",
            "When reflux after a sleeve is severe, converting to a bypass with hiatal repair at the same time is the most reliable way to get complete, lasting relief.",
            "When an obese patient has severe reflux due to a hiatal hernia, the standard of care is to perform hiatal hernia repair and gastric bypass. Hernia repair alone is not advised in that situation. The failure rate is too high.",
            "For a hiatal hernia that was previously repaired and came back, gastric bypass offers better outcomes than a redo hiatal repair for many patients.^1^",
          ],
        },
      ],
      subsections: [
        {
          id: "obesity-increases-abdominal-pressure",
          title: "Obesity raises the pressure inside the abdomen.",
          paragraphs: [],
        },
      ],
    },
    {
      heading: "When repair is recommended",
      id: "when-surgery-is-recommended",
      paragraphs: [
        "Not everybody needs their hiatal hernia repaired. In fact, most are best left alone.\u00a0Repair becomes part of the conversation when:",
      ],
      bulletGroups: [
        {
          items: [
            "Reflux persists despite anti-reflux medication.",
            "Long-term medication is not an option.",
            "Reflux has caused severe damage: esophagitis, stricture, or Barrett\u2019s esophagus.",
            "The hernia is large.",
            "The hernia is causing obstruction, bleeding, or anemia.",
            "Intolerable reflux develops after a sleeve gastrectomy.",
          ],
        },
      ],
    },
    {
      heading: "How hiatal hernia repair is done",
      id: "how-the-repair-is-done",
      paragraphs: [
        "The operation is performed laparoscopically, through small incisions. There are four main steps.",
        "**Reduce the hernia.** The stomach (and any other organ) is returned to the abdomen.",
        "**Cut out the sac.** The hernia sac is excised from the chest. Leaving it is a reason hernias recur.",
        "**Mobilize the esophagus.** The esophagus needs to reach into the abdomen with no tension. (Tension is the enemy of a lasting repair.) If it is stuck in the chest, it must be made un-stuck.",
        "**Close the crura.** The \u201ccrura\u201d are the sides of the hiatus.~1~ The oversized opening is brought back to normal with sutures. A mesh may be needed to close a huge hernia, but we try to get by without one. The outcomes evidence doesn\u2019t favor one way over the other.^1^",
        "**Fundoplication (a \u201cwrap\u201d).** In patients who have not had weight loss surgery, hiatal hernia repair normally includes this step. Fundoplication entails wrapping the fundus around the lower esophagus. Once wrapped, it is permanently sutured into place.",
      ],
      midSubsections: [
        {
          id: "about-wraps-and-fundoplication",
          title: "About wraps and fundoplication",
          paragraphs: [
            "How wraps work is hard to explain without diagrams and/or hand gestures, but they work. They effectively create a one-way valve at the top of the stomach. What you swallow can pass across it into the stomach, but it can\u2019t go the other way.",
            "Current guidelines favor doing a wrap with hiatal hernia repair. That guideline does not, however, apply when the patient has had weight-loss surgery. We normally skip it in bariatric patients, for good reason.",
            "For one thing, sleeve patients don\u2019t have a fundus anymore. For them, there\u2019s simply nothing to wrap.",
            "Bypass patients do have a fundus\u2014sort of. It\u2019s there, but it was separated from the pouch. Wrapping the disconnected fundus of a bypass patient has been tried, but it doesn\u2019t work as well. It provides no real benefit.~2~",
            "There are three ways to do a wrap, each named for its surgeon inventor. They include the Nissen, the Toupet, and the Dor. They all have different clinical uses, but that's a subject for another day.",
          ],
        },
      ],
    },

    {
      heading: "Hiatal hernia repair during weight-loss surgery",
      id: "repair-during-bariatric-surgery",
      paragraphs: [
        "When a hiatal hernia is found during a sleeve gastrectomy, it should usually be repaired then and there. The data shows that doing so makes post-op reflux much less likely.^2,3^",
        "Hernia repair doesn't guarantee a reflux-free life after a sleeve. In one meta-analysis, 12% of patients who had the combined operation developed new reflux they didn\u2019t have before. Another 11% had the hernia (and reflux) recur later.^2^",
        "The takeaway? If you are considering a sleeve and you already have reflux, you may want to consider a bypass instead. That advice applies regardless of hiatal hernia status. A sleeve with hernia repair often works out fine, but not always. You might be left holding the bag, heartburn-wise. Sometimes a bypass is the right answer.",
      ],
    },
    {
      heading: "Hiatal hernia and reflux after a gastric sleeve",
      id: "reflux-after-sleeve",
      paragraphs: [
        "If you had a sleeve gastrectomy and reflux has become a major issue, a hiatal hernia may be the problem. If so, repairing it has the potential to provide lasting relief.",
        "Diagnosing a hiatal hernia can be done with an upper GI contrast study, which is a five-minute x-ray. Sometimes upper endoscopy (a scope through the mouth) is also needed. pH testing is needed occasionally.",
      ],
    },
    {
      heading: "When hiatal hernias come back: recurrent hernias",
      id: "recurrent-hernias",
      paragraphs: [
        "We want every repair to last forever, but some hernias eventually come back. The medical journals put the rate somewhere between 10 and 50%—an unhelpfully broad range.~3~",
        "If your hiatal hernia comes back, it doesn’t mean your repair wasn’t a good one. Even master surgeons have repairs fail now and then.",
        "Several factors bear on the odds of failure. Among them are the size of the hernia, obesity, and the length of the esophagus. Of those factors, obesity is the only one we can try to proactively correct.",
        "Admittedly, 10 to 50% sounds high. Keep in mind that most are small, technical recurrences that do not require another repair. Studies tend to count minor (less than an inch) upward migrations of the stomach found on surveillance imaging. We don\u2019t normally do surveillance testing on asymptomatic patients. It\u2019s only done in clinical studies. They tend to try extremely hard to find recurrences.",
        "Some recurrences entail a more complete failure of the repair. They may be accompanied by return of pre-surgery symptoms or new, worse ones. These are the hernias we consider repairing again.",
        "We tend to think a little longer and harder before recommending a second or third repair to a patient. They are almost always feasible, but they entail more risk.",
        "For one thing, repeat operations take longer. Scar tissue from the prior repair tends to stick the liver, stomach, and diaphragm together. Those organs must be manually separated from each other before the repair can begin. It\u2019s work we didn\u2019t have to do the first time around, and it adds operative time. The longer the surgery is, the more likely certain complications (e.g., blood clots) become.",
        "A hernia that came back after one repair may benefit from re-repair. In some cases, conversion to a gastric bypass is the better answer. The gastric bypass remains the most reliable anti-reflux operation available. It works for people who have had weight loss surgery and those who haven\u2019t. Read more about it under [sleeve revision surgery](/services/revisions/sleeve) and [managing weight regain after bariatric surgery](/resources/managing-weight-regain-after-bariatric-surgery).",
      ],
    },
    {
      heading: "Recovery after hiatal hernia surgery",
      id: "recovery",
      paragraphs: [
        "Most patients go home the same day a hiatal hernia is repaired, or after one night if weight-loss surgery was done at the same time. People are ready to return to desk work in about a week, but there is no heavy lifting for four weeks. Your diet after surgery is liquids only for the first week. It then moves in stages through soft food to a normal diet over the next four weeks.",
      ],
      bulletGroups: [
        {
          items: [
            "Most patients go home the same day of hiatal hernia repair, from the recovery room.",
            "If you had bariatric surgery during the same operation, you can plan to spend one night in the hospital.",
            "You begin a staged diet right away: liquids, then soft foods, then a gradual return to normal over several weeks.",
            "No heavy lifting for 4 weeks.",
            "People typically can return to desk work after one week. Physical workers have a longer recovery.",
            "Some difficulty swallowing in the first weeks is expected. Be patient!",
          ],
        },
      ],
    },
    {
      heading: "Risks and expectations",
      id: "risks",
      paragraphs: [
        "Recurrence is what we strive to avoid. The hiatus is a moving part \u2014 it works with every breath and every swallow \u2014 and repairs can loosen over time. We know recurrence happens more with large hernias and in patients with high intra-abdominal pressure.",
        "Other uncommon risks: swallowing trouble, inability to belch or vomit, bloating, and injury to the esophagus or stomach. Also, there are the various risks of general anesthesia.",
      ],
    },
    {
      heading: "Insurance and cost",
      id: "insurance",
      paragraphs: [
        "Hiatal hernia repair for symptoms is generally covered as medically necessary. The approval pathway is different from, and less onerous than, the one for bariatric surgery. When hernia repair is to be performed at the same time as a weight-loss operation, the coverage question follows the bariatric operation. The [self-pay price](/self-pay-bariatric-surgery) is the same as sleeve gastrectomy.",
      ],
    },
    {
      heading: "Hiatal hernia surgery for patients across Virginia",
      id: "serving-virginia",
      paragraphs: [
        "Laparoscopic hiatal hernia repair is usually an ambulatory procedure at INOVA Fair Oaks Hospital in Fairfax. There is still the usual one-night stay for patients who get a sleeve or bypass at the same time.",
        "Many patients are referred by their gastroenterologist or primary care physician. We see referrals from across Virginia, including Richmond, Fredericksburg and Hampton Roads. Telehealth first visits are available.",
        "Patients also come to see Dr. Fitzer from Richmond, Charlottesville, Fredericksburg, Newport News, Norfolk, Virginia Beach, and Suffolk. Come talk to us!",
        "Hiatal hernia repair is often done at the same time as [gastric sleeve surgery in Northern Virginia](/services/sleeve) or [gastric bypass in Northern Virginia](/services/bypass).",
      ],
    },
  ],
  faqs: [
    {
      question: "Does a hiatal hernia always need surgery?",
      answer:
        "No. Most are small, cause few symptoms, and are managed with medication and changes to how and when you eat. Surgery is for when symptoms aren't controlled by medication or for hernias large enough to pose a risk if they remain unrepaired.",
    },
    {
      question: "Will hiatal hernia repair cure my heartburn?",
      answer:
        "Hopefully! It does for a lot of people, but it cannot be guaranteed. Usually, repair will at least improve reflux substantially. Many patients can stop medication entirely.",
    },
    {
      question: "Can a hiatal hernia come back?",
      answer:
        "Alas, yes. The hiatus moves with every breath, and repairs can loosen over time. Recurrence is more likely with large hernias. When a repair fails, a second repair is possible, and in selected patients conversion to a gastric bypass is the more durable answer.",
    },
    {
      question: "Can my hiatal hernia be fixed during weight-loss surgery?",
      answer:
        "Yes. If a hernia found during a sleeve or bypass deserves repair, it is normally done then and there, at the same operation. Repair of a hernia found during sleeve surgery lowers the odds the patient will have heartburn down the road.",
    },
    {
      question: "I had a sleeve and now I have terrible reflux. Is a hernia the cause?",
      answer:
        "Often it is, but not always. Some people develop reflux after their sleeve with no hiatal hernia present. Imaging can tell us what is going on most of the time. Now and then we need upper endoscopy to tell.\n\nIf there is no hiatal hernia, the sleeve itself is driving the reflux. If medications don't control it, converting to a gastric bypass is a reliable way to solve the problem.",
    },
  ],
  relatedPages: [
    {
      label: "Gastric sleeve revision",
      to: "/services/revisions/sleeve",
      note: "when reflux after a sleeve means the operation itself needs a second look.",
    },
    {
      label: "Sleeve gastrectomy",
      to: "/services/sleeve",
      note: "the first-time weight-loss operation hernias are most often found during.",
    },
    {
      label: "Gastric bypass",
      to: "/services/bypass",
      note: "the most reliable anti-reflux operation.",
    },
    {
      label: "Managing weight regain after bariatric surgery",
      to: "/resources/managing-weight-regain-after-bariatric-surgery",
      note: "what to do when weight returns after a prior operation.",
    },
    {
      label: "Insurance coverage",
      to: "/resources/insurance-coverage",
      note: "what plans ask for before approving surgery.",
    },
  ],
  closing:
    "Heartburn, regurgitation, and hiatal hernias can complicate your life and your weight-loss surgery plans. A good surgeon can quickly figure out what's going on and what the options are. Ready to talk? Request a consultation online or call (703) 709-9771.",
  lastReviewed: "2026-09-23",
  get byline() {
    const reviewed = formatReviewedDate(lastmodFor(HIATAL_PATH) ?? this.lastReviewed, "en");
    return `Last updated ${reviewed}.`;
  },
  footnotes: [
    "The word \u201ccrura\u201d is the plural form of \u201ccrus.\u201d Crus is actually the Latin word for \u201cleg.\u201d When you look at a picture of the crura, it kind of makes sense. The crura are muscular, long, and straight\u2014like legs. Also, there\u2019s a right one and a left one, and they meet at the top. Hence, the \u201clegs\u201d of your diaphragm.",
    "The physics behind it is not complex, but it\u2019s hard to explain without a diagram. Ask Dr. Fitzer about it if you meet him. He\u2019ll be delighted to sketch it out for you.",
    "The big variance between studies is due to differences in study design. Study designers must define what counts as a recurrent hernia. Some came up with loose definitions. Others were strict. Also, studies apply very different levels of effort to hunting for recurrences.",
  ],
  references: [
    "Daly S, Kumar SS, Collings AT, et al. SAGES guidelines for the surgical treatment of hiatal hernias. Surg Endosc. 2024;38(9):4765\u20134775. [doi:10.1007/s00464-024-11092-3](https://doi.org/10.1007/s00464-024-11092-3)",
    "Chen W, Feng J, Wang C, et al. Effect of concomitant laparoscopic sleeve gastrectomy and hiatal hernia repair on gastroesophageal reflux disease in patients with obesity: a systematic review and meta-analysis. Obes Surg. 2021;31(9):3905\u20133918. [doi:10.1007/s11695-021-05545-0](https://doi.org/10.1007/s11695-021-05545-0)",
    "Kumar A, Madhav JV, Singla V, Monga S, Aggarwal S. Concomitant hiatal hernia repair with sleeve gastrectomy: impact on gastroesophageal reflux? Surg Laparosc Endosc Percutan Tech. 2023;33(5):435\u2013439. [doi:10.1097/SLE.0000000000001216](https://doi.org/10.1097/SLE.0000000000001216)",
  ],
};
